import { Link } from "react-router-dom";
import { Check, PlayCircle, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  SIGNUP_SOURCE_OPTIONS,
  useDismissOnboarding,
  useOnboarding,
  useSetSignupSource,
} from "@/hooks/use-onboarding";
import type { Project } from "@/lib/api-types";

type Props = {
  /** "/admin/projects" — project links + watch prefix are derived from it. */
  basePath: string;
  projects: Project[] | undefined;
  onCreateProject: () => void;
};

/**
 * "Get started" checklist (2026-10-31). Owner-only (server returns
 * eligible:false for other roles). Steps come from real data; the card hides
 * once dismissed. Also asks "How did you find Editlock?" once.
 */
export function OnboardingCard({ basePath, projects, onCreateProject }: Props) {
  const { data } = useOnboarding();
  const dismiss = useDismissOnboarding();
  const setSource = useSetSignupSource();

  if (!data || !data.eligible || data.dismissed) return null;

  const prefix = basePath.replace(/\/projects$/, "");
  const firstReal = (projects ?? []).find((p) => !p.isSample);
  const realHref = firstReal ? `${basePath}/${firstReal.id}` : null;
  const sampleHref = data.sampleVideoId ? `${prefix}/watch/${data.sampleVideoId}` : null;
  const { steps } = data;
  const done = [steps.createProject, steps.upload, steps.share].filter(Boolean).length;

  const items: { label: string; hint: string; done: boolean; action: React.ReactNode }[] = [
    {
      label: "Create your first project",
      hint: "One project per client or campaign works well.",
      done: steps.createProject,
      action: (
        <Button size="sm" variant="outline" onClick={onCreateProject}>
          New project
        </Button>
      ),
    },
    {
      label: "Upload a video",
      hint: "Drop in a real edit you're sending a client this week.",
      done: steps.upload,
      action: realHref ? (
        <Button size="sm" variant="outline" asChild>
          <Link to={realHref}>Open project</Link>
        </Button>
      ) : null,
    },
    {
      label: "Send the review link to your client",
      hint: "Click Share on a video, copy the link, send it on WhatsApp. No login needed for them.",
      done: steps.share,
      action: realHref && steps.upload ? (
        <Button size="sm" variant="outline" asChild>
          <Link to={realHref}>Share a video</Link>
        </Button>
      ) : null,
    },
  ];

  return (
    <div className="relative mb-6 rounded-xl border border-border bg-card p-4 md:p-5">
      <button
        type="button"
        aria-label="Dismiss getting started"
        onClick={() => dismiss.mutate()}
        className="absolute right-3 top-3 grid h-7 w-7 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
      >
        <X className="h-4 w-4" />
      </button>

      <div className="flex flex-col gap-3 pr-8 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="font-display text-lg font-semibold">
            {data.complete ? "You're all set 🎉" : "Get your first client review done"}
          </h2>
          <p className="text-sm text-muted-foreground">
            {data.complete
              ? steps.clientOpened
                ? "Your client opened the link. Their comments will land right on the frame."
                : "Link sent. You'll get a notification when your client comments."
              : `${done} of 3 done · takes about 5 minutes`}
          </p>
        </div>
        {sampleHref && !data.complete && (
          <Button size="sm" asChild>
            <Link to={sampleHref}>
              <PlayCircle className="h-4 w-4" />
              See how it works
            </Link>
          </Button>
        )}
      </div>

      {!data.complete && (
        <ol className="mt-4 flex flex-col divide-y divide-border">
          {items.map((it, i) => (
            <li key={it.label} className="flex items-center gap-3 py-3">
              <span
                className={cn(
                  "grid h-6 w-6 shrink-0 place-items-center rounded-full border text-xs font-semibold",
                  it.done
                    ? "border-transparent bg-primary text-primary-foreground"
                    : "border-border text-muted-foreground",
                )}
              >
                {it.done ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : i + 1}
              </span>
              <div className="min-w-0 flex-1">
                <div className={cn("text-sm font-medium", it.done && "text-muted-foreground line-through")}>
                  {it.label}
                </div>
                {!it.done && <div className="text-xs text-muted-foreground">{it.hint}</div>}
              </div>
              {!it.done && it.action}
            </li>
          ))}
        </ol>
      )}

      {data.askSource && (
        <div className="mt-3 border-t border-border pt-3">
          <div className="mb-2 text-xs text-muted-foreground">Quick one: how did you find Editlock?</div>
          <div className="flex flex-wrap gap-1.5">
            {SIGNUP_SOURCE_OPTIONS.map((o) => (
              <button
                key={o.value}
                type="button"
                disabled={setSource.isPending}
                onClick={() => setSource.mutate(o.value)}
                className="rounded-full border border-border px-3 py-1 text-xs text-muted-foreground transition-colors hover:border-foreground/40 hover:text-foreground disabled:opacity-50"
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
